import { Box, Button, Grid, IconButton, Paper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { catchErrorMessage } from 'utility/helpers';
import { ConfigType } from 'config/config';
import { useNavigate } from 'react-router-dom';
import ConfigDataGrid from '@buildwise/pages/admin/userAdministration/components/DataTable';
import { GridColDef } from '@mui/x-data-grid';
import { useConfigQuery, ConfigAPI } from '@buildwise/libs/configurations';
import Edit2LineIcon from 'remixicon-react/Edit2LineIcon';
import { SELECTED_BUILDWISE_CONFIGURATION } from 'constants/routes';
import EditConfigModal from '@buildwise/libs/company-admin/components/configurations/EditConfigModal';
import AddLineIcon from 'remixicon-react/AddLineIcon';

const BuildWiseConfigurations = () => {
  const navigate = useNavigate();
  const { configs, updateConfigCache, isLoading } = useConfigQuery({ productLevel: true });
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<ConfigType>();
  const [loading, setLoading] = useState<boolean>(false);

  const handleEditClick = (config) => {
    setSelectedRow(config);
    setShowEditModal(true);
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'actions',
        headerName: '',
        renderCell: (params) => (
          <IconButton
            sx={{ margin: '0 auto' }}
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(params.row);
            }}
          >
            <Edit2LineIcon />
          </IconButton>
        ),
        sortable: false,
        disableColumnMenu: true,
      },
      { field: 'id', headerName: 'ID', width: 30, hide: true, disableColumnMenu: true },
      {
        field: 'configName',
        headerName: 'Config Name',
        width: 250,
        sortable: true,
        disableColumnMenu: true,
      },
      {
        field: 'description',
        headerName: 'Description',
        width: 250,
        sortable: true,
        disableColumnMenu: true,
      },
    ],
    [configs],
  );

  const handleUpdateConfig = async (updateConfig: ConfigType) => {
    try {
      setLoading(true);
      const { data } = await ConfigAPI.updateConfig({
        ...updateConfig,
      });
      const oldConfig = configs.find((config) => config?.configName === data.config?.configName);
      toast.success(`${data?.config?.configName} ${oldConfig ? 'updated' : 'added'} successfully`);
      const updatedConfig = oldConfig
        ? configs?.map((config) =>
            data?.config?.configName === config?.configName ? data?.config : config,
          )
        : data?.config
          ? [...configs, data.config]
          : [...configs];
      updateConfigCache({
        updatedConfig: { configs: updatedConfig },
      });
    } catch (e) {
      toast.error(catchErrorMessage(e));
    } finally {
      setLoading(false);
      setShowEditModal(false);
    }
  };

  return (
    <>
      <Container>
        <Box sx={{ padding: 4 }}>
          <Grid container>
            <Grid item md={12} xs={12}>
              <Box
                display="flex"
                paddingX={0}
                paddingY={2}
                justifyContent="space-between"
                alignItems="center"
                sx={{ position: 'relative' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <Typography sx={{ fontSize: 19, fontWeight: 600 }}>Configurations</Typography>
                </Box>
                <$Button
                  startIcon={<AddLineIcon />}
                  onClick={() => {
                    handleEditClick({});
                  }}
                  variant="outlined"
                >
                  Add Configuration
                </$Button>
              </Box>
              <ConfigDataGrid<ConfigType>
                columns={columns}
                rows={configs}
                pageSize={10}
                height={630}
                checkboxSelection={false}
                onRowClick={(config) => {
                  navigate(
                    SELECTED_BUILDWISE_CONFIGURATION.replace(':configName', config?.configName),
                    { state: config },
                  );
                }}
                customStyles={{
                  '& .MuiDataGrid-row:hover': {
                    cursor: 'pointer',
                  },
                }}
                loading={isLoading}
              />
            </Grid>
          </Grid>
        </Box>
      </Container>
      {showEditModal && selectedRow && (
        <EditConfigModal
          open={showEditModal}
          setOpen={setShowEditModal}
          config={selectedRow}
          onSave={handleUpdateConfig}
          loading={loading}
        />
      )}
    </>
  );
};

const Container = styled(Paper, { label: 'Container' })(({ theme }) => ({
  background: theme.palette.custom.white,
  borderRadius: 16,
  boxShadow: `0 4px 24px ${theme.palette.custom.boxShadow}`,
  marginBottom: 24,
  overflow: 'hidden',
}));

const $Button = styled(Button, { label: 'BuildWiseAdminAction' })(() => ({
  height: 42,
  borderRadius: 33,
  fontSize: 14,
}));

export default BuildWiseConfigurations;
